/**
 * Profile API Client
 *
 * API calls for profile management and publishing.
 */

const API_BASE = import.meta.env.VITE_API_URL || 'https://pxtxt.itsmin.workers.dev';

interface ApiOptions {
	method?: 'GET' | 'POST';
	body?: unknown;
}

async function apiCall<T>(token: string, endpoint: string, options: ApiOptions = {}): Promise<T> {
	const { method = 'GET', body } = options;

	const response = await fetch(`${API_BASE}${endpoint}`, {
		method,
		headers: {
			'Content-Type': 'application/json',
			'Authorization': `Bearer ${token}`,
		},
		body: body ? JSON.stringify(body) : undefined,
	});

	if (!response.ok) {
		const error = await response.json().catch(() => ({ error: 'Request failed' }));
		const details = error.errors?.length ? `: ${error.errors.join(', ')}` : '';
		throw new Error((error.error || error.message || 'Request failed') + details);
	}

	return response.json();
}

// Types
export interface Profile {
	id: string;
	slug: string;
	displayName: string;
	isPublic: boolean;
	chatEnabled: boolean;
	resumeUrl: string | null;
	linkedinUrl: string | null;
	portfolioUrl: string | null;
	allowedOrigins: string[];
	createdAt: number;
	updatedAt: number;
	publishedAt: number | null;
}

interface Snapshot {
	id: string;
	version: number;
	claimCount: number;
	generatedAt: number;
	isActive?: boolean;
}

export type DepthTier = 'below' | 'thin' | 'developed' | 'rich';

export interface ProfileDepth {
	tier: DepthTier;
	canRepresent: boolean;
	canReflect: boolean;
	signals: {
		claimCount: number;
		companyCount: number;
		highlightCount: number;
		hasIdentity: boolean;
		hasSubstantiveWork: boolean;
	};
	floorReasons: string[];
	guidance: string;
}

interface ProfileResponse {
	profile: Profile | null;
	snapshot: Snapshot | null;
	depth?: ProfileDepth;
}

interface ProfileUpdate {
	slug?: string;
	displayName?: string;
	resumeUrl?: string | null;
	linkedinUrl?: string | null;
	portfolioUrl?: string | null;
	allowedOrigins?: string[];
	chatEnabled?: boolean;
}

interface PublishResult {
	success: boolean;
	slug: string;
	snapshot: {
		id: string;
		version: number;
		claimCount: number;
	};
	warnings: string[];
}

// ── Pre-publish review (Session #149) ────────────────────────────────────

export type EntityChangeKind = 'added' | 'removed' | 'modified';
export type EntityChangeType = 'identity' | 'work' | 'person' | 'event' | 'project' | 'skill';

export interface EntityChange {
	kind: EntityChangeKind;
	entityType: EntityChangeType;
	displayName: string;
	/** Present only for kind='modified'. Pre-formatted human-readable bullets. */
	changes?: string[];
}

export interface PublishPreview {
	canPublish: boolean;
	floorReasons: string[];
	hasActiveSnapshot: boolean;
	slug: string;
	publishUrl: string;
	activeSnapshot: {
		version: number;
		generatedAt: number;
		claimCount: number;
	} | null;
	nextSnapshot: {
		claimCount: number;
		workHistoryCount: number;
		peopleCount: number;
		projectCount: number;
		eventCount: number;
	};
	changes: EntityChange[];
	sideEffects: {
		narrativeWillRegenerate: boolean;
		companiesNeedingEnrichment: string[];
	};
}

export const profileApi = {
	async getProfile(token: string): Promise<ProfileResponse> {
		try {
			return await apiCall<ProfileResponse>(token, '/api/profile');
		} catch (err) {
			// Return null profile for 404 (not created yet)
			if (err instanceof Error && err.message.includes('not found')) {
				return { profile: null, snapshot: null };
			}
			throw err;
		}
	},

	async createOrUpdateProfile(token: string, data: ProfileUpdate): Promise<{ profile: Profile }> {
		return apiCall<{ profile: Profile }>(token, '/api/profile', {
			method: 'POST',
			body: data,
		});
	},

	async checkSlug(token: string, slug: string): Promise<{ slug: string; available: boolean }> {
		return apiCall<{ slug: string; available: boolean }>(token, `/api/profile/check-slug/${slug}`);
	},

	async publish(token: string): Promise<PublishResult> {
		return apiCall<PublishResult>(token, '/api/profile/publish', {
			method: 'POST',
		});
	},

	async publishPreview(token: string): Promise<PublishPreview> {
		return apiCall<PublishPreview>(token, '/api/profile/publish/preview');
	},

	async unpublish(token: string): Promise<{ success: boolean }> {
		return apiCall<{ success: boolean }>(token, '/api/profile/unpublish', {
			method: 'POST',
		});
	},

	async listSnapshots(token: string): Promise<{ snapshots: Snapshot[] }> {
		return apiCall<{ snapshots: Snapshot[] }>(token, '/api/profile/snapshots');
	},

	async activateSnapshot(token: string, snapshotId: string): Promise<{ success: boolean }> {
		return apiCall<{ success: boolean }>(token, `/api/profile/snapshots/${snapshotId}/activate`, {
			method: 'POST',
		});
	},
};

/**
 * Fetch public narrative (no auth required).
 */
export async function fetchPublicNarrative(slug: string): Promise<{
	generatedAt: number;
	format: string;
	depth: string;
	sections: { type: string; heading: string; content: string; company?: string }[];
	provenance: { claimCount: number; sessionCount: number };
} | null> {
	try {
		const response = await fetch(`${API_BASE}/api/public/narrative/${slug}`);
		if (!response.ok) return null;
		return response.json();
	} catch {
		return null;
	}
}
